import * as Rest from "../Generated/RestClient";
import {parseUrl, Url} from "../../../Intercom.ReactUI/Utils/Url";
import { Method } from './HttpMethods';
import { pollerPromise } from "../Models/PollingModel";

export interface Subscription {
    id: string,
    subscriptionId: string,
    displayName: string,
    state: string
}

export interface Region {
    displayName: string,
    name: string
}

export interface AppSerivcePlan {
    id: string,
    name: string,
    kind: string,
    location: string,
    sku: {
        name: string,
        tier: string,
        size: string,
        family: string,
        capacity: number
    }
}

export interface WebAppService {
    id: string,
    name: string,
    type: string,
    kind: string,
    location: string,
    properties: {
        serverFarmId: string
    }
}

export interface ArmResource<T> {
    name: string,
    id: string,
    properties: T
}

export interface SourceControl {
    repoUrl: string,
    branch: string,
    isManualIntegration: boolean,
    deploymentRollbackEnabled: boolean,
    isMercurial: boolean,
    provisioningState?: string
    token?: string
}

export interface ArmResponse<T> {
    value: T;
}

export interface TenantId {
    id: string,
    tenantId: string,
    displayName: string,
    countryCode: string,
    domains: string[]
}

export interface ResourceGroup {
    id: string;
    name: string;
    location: string;
    properties: {
        provisioningState: string;
    }
}

export interface WebAppSettings {
    id: string;
    name: string;
    type: string;
    location: string;
    properties: {
        AzureWebJobsStorage?: string;
        AzureWebJobsDashboard?: string;
        AzureWebJobsDisableHomepage?: string;
        WEBSITE_CONTENTAZUREFILECONNECTIONSTRING_TEMP?: string;
        WEBSITE_CONTENTAZUREFILECONNECTIONSTRING?: string;
        WEBSITE_CONTENTSHARE?: string;
        FUNCTIONS_EXTENSION_VERSION?: string;
        WEBSITE_NODE_DEFAULT_VERSION?: string;
        BotId?: string;
        BotEnv?: string;
        BotStateEndpoint?: string;
        BotOpenIdMetadata?: string;
        MicrosoftAppId?: string;
        MicrosoftAppPassword?: string;
        SCM_POST_DEPLOYMENT_ACTIONS_PATH?: string;
        BotDevAppInsightKey?: string;
        BotDevAppInsightsKey?: string;
        BotDevAppInsightsName?: string;
        BotDevAppInsightsAppId?: string;
        LuisAppId?: string;
        LuisAPIKey?: string;
        LuisAPIHostName?: string;
    }
}

export interface ApplicationInsights {
    id: string;
    name: string;
    type: string;
    location: string;
    tags: { [key: string]: string };
    kind: string;
    etag?: string;
    properties?: any;
}

export type EtaggedResource = ApplicationInsights;

export interface DeploymentProperties {
    provisioningState: string,
    outputResources: {id: string}[]
}

const armApiVersion = '2014-04-01'
const tenantApiVersion = '2018-01-01'
const storageApiVersion = '2015-05-01-preview';
const appInsightsApiVersion = '2015-05-01';
const websiteApiVersion = '2015-08-01';
const aspApiVersion = '2016-03-01';
const sourceControlApiVersion = '2016-08-01';
const deploymentApiVersion = '2017-05-10';
const botServiceApiVersion = '2017-12-01';

export class ArmClient {
    public armRoot: Url;
    constructor(private token: string) {
        this.armRoot = parseUrl('https://management.azure.com');
    }

    public get<T>(url: string): JQueryPromise<T> {
        return this.request(url, Method.GET);
    }

    public post<T>(url: string, data?: any): JQueryPromise<T> {
        return this.request(url, Method.POST, data);
    }

    public patch<T>(url: string, data?: any): JQueryPromise<T> {
        return this.request(url, Method.PATCH, data);
    }

    public delete<T>(url: string): JQueryPromise<T> {
        return this.request(url, Method.DELETE);
    }

    public put<T>(url: string, data?: any, includeIfMatch: boolean = true): JQueryPromise<T> {
        return this.request(url, Method.PUT, data, includeIfMatch);
    }

    public head<T>(url: string): JQueryPromise<T> {
        return this.request(url, Method.HEAD);
    }

    public getSubscriptions(): JQueryPromise<ArmResponse<Subscription[]>> {
        const url = `${this.armRoot.format()}/subscriptions?api-version=${armApiVersion}`;
        return this.get(url);
    }

    public getSubscription(subscriptionId: string) : JQueryPromise<Subscription> {
        const url = `${this.armRoot.format()}/subscriptions/${subscriptionId}?api-version=${armApiVersion}`;
        return this.get(url);
    }

    public getRegions(subscriptionId: string): JQueryPromise<ArmResponse<ArmResource<Region>[]>> {
        const url = `${this.armRoot.format()}/subscriptions/${subscriptionId}/providers/Microsoft.Web/georegions?sku=Dynamic&api-version=${websiteApiVersion}`;
        return this.get(url);
    }

    public getAppServicePlans(subscriptionId: string) : JQueryPromise<ArmResponse<AppSerivcePlan[]>> {
        const url = `${this.armRoot.format()}/subscriptions/${subscriptionId}/providers/Microsoft.Web/serverfarms?api-version=${aspApiVersion}`;
        return this.get(url);
    }

    public getResourceGroups(subscriptionId: string): JQueryPromise<ArmResponse<ResourceGroup[]>> {
        const url = `${this.armRoot.format()}/subscriptions/${subscriptionId}/resourceGroups?api-version=${armApiVersion}`;
        return this.get(url);
    }

    public getWebAppService(rsrcId: string) : JQueryPromise<WebAppService> {
        const url = `${this.armRoot.format()}${rsrcId}?api-version=${websiteApiVersion}`;
        return this.get(url);
    }

    public getWebAppSettings(subId: string, rgName: string, appId: string) : JQueryPromise<WebAppSettings> {
        const url = `${this.armRoot.format()}/subscriptions/${subId}/resourceGroups/${rgName}/providers/Microsoft.Web/sites/${appId}/config/appsettings/list?api-version=${websiteApiVersion}`;
        return this.post(url);
    }

    public getAppInsights(subId: string, rgName: string, insightsName: string) : JQueryPromise<ApplicationInsights> {
        const url = `${this.armRoot.format()}/subscriptions/${subId}/resourceGroups/${rgName}/providers/microsoft.insights/components/${insightsName}?api-version=${appInsightsApiVersion}`;
        return this.get(url);
    }
    
    public updateAppInsightsTags(subId: string, rgName: string, insightsName: string, tags: { [key: string]: string }): JQueryPromise<ApplicationInsights> {
        const url = `${this.armRoot.format()}/subscriptions/${subId}/resourceGroups/${rgName}/providers/microsoft.insights/components/${insightsName}?api-version=${appInsightsApiVersion}`;
        return this.patch(url, {
            tags: tags
        });
    }

    public isAppNameAvailable(subscriptionId: string, appName: string): JQueryPromise<ArmResource<boolean>> {
        const url = `${this.armRoot.format()}/subscriptions/${subscriptionId}/providers/Microsoft.Web/ishostnameavailable/${appName}?api-version=${websiteApiVersion}`;
        return this.get(url);
    }

    public deleteDeployment(deploymentId: string): JQueryPromise<any> {
        const url = `${this.armRoot.format()}${deploymentId}?api-version=${deploymentApiVersion}`;
        // delete call does not return JSON which makes JQuery AJAX throw an error resulting in hitting a .fail() method
        return this.requestVoidResult(url, Method.DELETE);
    }

    public deleteWebApp(subscriptionId: string, rgName: string, appName: string): JQueryPromise<any> {
        const url = `${this.armRoot.format()}/subscriptions/${subscriptionId}/resourceGroups/${rgName}/providers/Microsoft.Web/sites/${appName}?api-version=${sourceControlApiVersion}`;
        // delete call does not return JSON which makes JQuery AJAX throw an error resulting in hitting a .fail() method
        return this.requestVoidResult(url, Method.DELETE);
    }

    public createResourceGroup(subId: string, rgName: string, locationName: string): JQueryPromise<any> {
        const url = `${this.armRoot.format()}/subscriptions/${subId}/resourceGroups/${rgName}?api-version=${deploymentApiVersion}`;
        return this.put(url, { location: locationName });
    }

    public checkResourceGroupExists(subId: string, rgName: string): JQueryPromise<boolean> {
        const url = `${this.armRoot.format()}/subscriptions/${subId}/resourceGroups/${rgName}?api-version=${deploymentApiVersion}`;
        var $deferred = $.Deferred<boolean>();
        // will return 204 if the RG exists; 404 if it doesn't
        this.head(url)
            .done(() => $deferred.resolve(true))
            .fail(() => $deferred.resolve(false));

        return $deferred;
    }

    public checkDeploymentResult(deployment: ArmResource<any>) : JQueryPromise<ArmResource<DeploymentProperties>> {
        const url = `${this.armRoot.format()}${deployment.id}?api-version=${deploymentApiVersion}`;
        const result = $.Deferred<ArmResource<DeploymentProperties>>();
        this.get(url).then((x: ArmResource<DeploymentProperties>) => {
            const curState = x.properties ? x.properties.provisioningState : null;
            if (curState === 'Failed' || curState === 'Succeeded') {
                result.resolve(x);
            } else {
                result.reject(curState);
            }
        }, e => result.reject(e));
        return result;
    }

    public putBotService(subId: string, rgName: string, deploymentName: string, data?: any): JQueryPromise<EtaggedResource> {
        const url = `${this.armRoot.format()}/subscriptions/${subId}/resourcegroups/${rgName}/providers/microsoft.resources/deployments/${deploymentName}?api-version=${deploymentApiVersion}`;
        const deployment = this.put(url, data, false);
        const checkDeployment = deployment.then((newDeployment: ArmResource<any>) => pollerPromise(() =>  this.checkDeploymentResult(newDeployment), 5000, 120000));
        const fetchBotResource = checkDeployment
            .then((deploymentResult: ArmResource<DeploymentProperties>) => {
                if (deploymentResult.properties.provisioningState === 'Succeeded') {
                    const botResourceId = deploymentResult.properties.outputResources[0].id;
                    const botResourceUrl = `${this.armRoot.format()}${botResourceId}?api-version=${botServiceApiVersion}`;
                    return this.get<EtaggedResource>(botResourceUrl);
                } else {
                    return $.Deferred<EtaggedResource>().reject(deploymentResult);
                }
            });
        return fetchBotResource;
    }

    public setupContinuousIntegration(rsrcId: string, sourceControl: SourceControl) : JQueryPromise<ArmResource<SourceControl>> {
        const url = `${this.armRoot.format()}/${rsrcId}/sourcecontrols/web?api-version=${sourceControlApiVersion}`;
        return this.request(url, Method.PUT, {
            id: rsrcId,
            type: "Microsoft.Web/sites/sourcecontrols",
            properties: sourceControl
        });
    }

    public getSourceControl(rsrcId: string) : JQueryPromise<ArmResource<SourceControl>> {
        const url = `${this.armRoot.format()}/${rsrcId}/sourcecontrols/web?api-version=${sourceControlApiVersion}`;
        return this.get(url);
    }

    public get tenants() : JQueryPromise<TenantId[]> {
        const url = `${this.armRoot.format()}/tenants?api-version=${tenantApiVersion}`;
        return this.get(url)
            .then((x : ArmResponse<TenantId[]>) => {
                return $.Deferred<TenantId[]>().resolve(x.value);
            });
    }
    
    private request<T>(url: string, method: Method, data?: any, includeIfMatch: boolean = true): JQueryPromise<T> {
        this.valideUrl(url);
        const reqHeaders = includeIfMatch ? 
            {
                'Authorization': this.getAuthHeader(),
                'If-Match': '*',
                'Accept': 'application/json,*/*'
            } :
            {
                'Authorization': this.getAuthHeader(),
                'Accept': 'application/json,*/*'
            };

        return $.ajax({
            url: url,
            type: Method[method] as string,
            headers: reqHeaders,
            dataType: 'json',
            contentType: 'application/json',
            beforeSend: () => Rest.showNetworkIcon(true),
            data: data ? JSON.stringify(data) : data
        }).always(() => Rest.showNetworkIcon(false));
    }

    private requestVoidResult<T>(url: string, method: Method, data?: any): JQueryPromise<T> {
        this.valideUrl(url);
        const reqHeaders = {
            'Authorization': this.getAuthHeader(),
            'If-Match': '*',
            'Accept': 'application/json,*/*'
        };

        return $.ajax({
            url: url,
            type: Method[method] as string,
            headers: reqHeaders,
            dataType: 'text',
            contentType: 'application/json',
            beforeSend: () => Rest.showNetworkIcon(true),
            data: data ? JSON.stringify(data) : data
        }).always(() => Rest.showNetworkIcon(false));
    }

    private valideUrl(url: string) {
        const uri = parseUrl(url);
        if (uri.scheme != this.armRoot.scheme || uri.hostname != this.armRoot.hostname) {
            throw 'non conforming url';
        }
    }

    private getAuthHeader(): string {
        return `Bearer ${this.token}`;
    }
}



// WEBPACK FOOTER //
// ./Client/Services/ArmApi.ts